import axios from 'axios';
import { storage, UrlService } from '@nuskin/ns-util';
import { getConfiguration } from '@nuskin/configuration-sdk';
import { CustomerTypes } from '@nuskin/ns-product-lib';
import { ProductDataService } from '@nuskin/ns-product';

const EQUINOX_FAVORITES_LIST_NAME = 'MyFavorites'; //this is inline with what skava is using
const PENDING_FAVORITE = "pendingFavorite";

const defaultHeaders = {
    'Accept': '*',
    'Content-Type': 'application/json'
}

/**
 * getEquinoxSession - get equinox session details
 * @returns { Promise<equinoxRequest> }
 */

async function getEquinoxSession() {

    const equinoxRequestOptions = {
        method: 'GET',
        endpoint: 'customers'
    }

    return equinoxRequest(equinoxRequestOptions);
}

/**
 * Maps Equinox session details to a given session object
 *
 * @param {Object} sessionDetails - The session object to map the details to
 * @returns {Object} The updated session object with the mapped details
 */
async function equinoxSessionDetailMapper(sessionDetails) {
    // Get Equinox user data
    const { data: equinoxUserData } = await getEquinoxSession();

    // Get the Equinox user's account type
    const accountType = getEquinoxAccountType(equinoxUserData.accounts.accountType);

    // Map the Equinox user data to the session details object
    sessionDetails.firstName = equinoxUserData.firstName;
    sessionDetails.lastName = equinoxUserData.lastName;
    sessionDetails.localName = `${equinoxUserData.firstName} ${equinoxUserData.lastName}`;
    sessionDetails.displayName = `${equinoxUserData.firstName} ${equinoxUserData.lastName}`;
    sessionDetails.accountType = accountType;

    // Return the updated session details object
    return sessionDetails;
}

/**
 * @note re-map customer types due to data migration. Used updated customer types
 * in ns-product-lib
 *
 * Maps the account type of an Equinox user to a standardized string
 * @param {string} equinoxAccountType - The account type of the Equinox user
 * @returns {string} The mapped account type
 */
function getEquinoxAccountType(equinoxAccountType) {
    if (!equinoxAccountType) {
        return "";
    }
    const customerType = equinoxAccountType.trim().toLowerCase();
    if (customerType === CustomerTypes.BrandAffiliate) {
        return 'DIST';
    } else if (customerType === CustomerTypes.Retail) {
        return 'CUST';
    } else if (customerType === CustomerTypes.PreferredCustomer ||
        customerType === CustomerTypes.Preferred) {
        return 'PREF';
    }

    return '';
}

/**
 * createEquinoxFavorite - creates list in equinox for MySite Favorites
 * @returns {Promise.<equinoxRequest>} a promise resolving to the response of the request
 */
async function createEquinoxFavorite() {

    const equinoxRequestOptions = {
        method: 'POST',
        endpoint: 'listservice/lists',
        headers: defaultHeaders,
        data: {
            listItems: [],
            name: EQUINOX_FAVORITES_LIST_NAME,
            accessType: 'PUBLIC',
            type: 'FAVORITES',
            // SKU Item type is for the other "lists" on the equionx platform,
            // PRODUCT Item type is for "favorites" (learned from Anto Charly Arul Santhiyagoo)
            itemType: 'PRODUCT'
        }
    };

    return equinoxRequest(equinoxRequestOptions)
}

/**
 * getEquinoxFavorite - retrieves list in equinox for MySite Favorites
 * @returns {Promise.<equinoxRequest>} a promise resolving to the response of the request
 */
async function getEquinoxFavorite() {

    const equinoxRequestOptions = {
        method: 'GET',
        endpoint: `listservice/lists`,
        params: {
            size: 10,
            page: 1
        }
    };

    const equinoxFavoriteResponse = await equinoxRequest(equinoxRequestOptions)
    return equinoxFavoriteResponse.data.lists.find((list) => list.name === EQUINOX_FAVORITES_LIST_NAME);
}

/**
 * addItemToEquinoxList - adds item to a list in equinox for MySite Favorites
 * @param {string} sku - the SKU of the item to add to the list
 * @returns {Promise.<equinoxRequest>} a promise resolving to the response of the request
 */
async function addItemToEquinoxList(sku) {
    //@TODO transfer this to ns-util meta data
    const equinoxListId = sessionStorage.getItem('equinoxListId')
    const productData = await ProductDataService.getProductData(sku);
    let productId = sku;

    if(Array.isArray(productData.products) && productData.products.length > 0 && productData.products[0]) {
        productId = productData.products[0].equinoxProductId
    }

    const equinoxRequestOptions = {
        method: 'POST',
        endpoint: `listservice/lists/${equinoxListId}/items`,
        data: [
            {
                key: productId,
                name: sku
            }
        ]
    };

    return equinoxRequest(equinoxRequestOptions);
}

/**
 * Retrieves the user's favorite items from the Equinox API, or creates a new list if one does not exist.
 * Also checks for any pending favorite items in local storage and adds them to the list if found.
 */
async function getOrCreateFavorites() {
    // Attempt to retrieve the user's favorite list from the API
    let userFavorites = await getEquinoxFavorite();

    // If no list is found, create a new one
    if(!userFavorites) {
        const createdList = await createEquinoxFavorite();
        console.log('List not found, creating one...' , createdList)
        userFavorites = await getEquinoxFavorite();
    }

    // Store the list ID in session storage
    if(userFavorites) {
        sessionStorage.setItem('equinoxListId', userFavorites.listId)
    }

    // Check for any pending favorite items in local storage
    const pendingFavorite = storage.getItem(PENDING_FAVORITE);

    // If a pending favorite item is found, add it to the user's favorite list
    if(pendingFavorite && pendingFavorite.sku) {
        try {
            await addItemToEquinoxList(pendingFavorite.sku)
        } catch (e) {
            console.error(e);
        } finally {
            // Remove the pending favorite item from local storage
            storage.removeItem(PENDING_FAVORITE)
        }
    }
}

/**
 * removeItemInEquinoxList - removes item from a list in equinox for MySite Favorites
 * @param {string} sku - the SKU of the item to remove from the list
 * @returns {Promise.<_equinoxRequest>} a promise resolving to the response of the request
 */
async function removeItemInEquinoxList(sku) {

    const localFavorites = JSON.parse(sessionStorage.getItem('favorites-list'));

    //@TODO transfer this to ns-util meta data
    const equinoxListId = sessionStorage.getItem('equinoxListId')

    const listItem = localFavorites && localFavorites.favorites ?
        localFavorites.favorites.find((favorite) => favorite.sku === sku) : null;

    const equinoxRequestOptions = {
        method: 'DELETE',
        endpoint: `listservice/lists/${equinoxListId}/items/${listItem ? listItem.equinoxListItemId : null}`
    };

    // need to remove the item not only from equinox via equinoxRequest but from the favorites-list as well
    const updatedLocalFavorites = localFavorites && localFavorites.favorites ?
        localFavorites.favorites.filter(function(obj) {return obj.sku !== sku}) : null;

    sessionStorage.setItem('favorites-list', JSON.stringify({...updatedLocalFavorites}));

    return equinoxRequest(equinoxRequestOptions);
}

//TODO transfer equinoxRequest into a library
/**
 * equinoxRequest - equinox axios facade
 * @param { Array } options - [{
 *              method: http method,
 *              endpoint: the equinox api endpoint
 *              params: get params
 *              data: post data
 *          }]
 * @returns { Object }
 */
async function equinoxRequest(options) {

    const { equinoxMarket, equinoxAPIUrl, marketLocale } = await getEquinoxRequestConfiguration();

    const defaultEquinoxParams = {
        locale: marketLocale,
        storeId: equinoxMarket.store_id
    }

    let axiosRequest = {
        method: options.method,
        url: `${equinoxAPIUrl}/orchestrationservices/storefront/${options.endpoint}`,
        headers: defaultHeaders,
        withCredentials: (options.method !== "DELETE"),
        params: (options.params) ? { ...defaultEquinoxParams, ...options.params} : defaultEquinoxParams
    };

    if(options.data) {
        axiosRequest.data = options.data
    }


    return axios(axiosRequest)

}

/**
 * getEquinoxRequestConfiguration - fetch equinox market and kong url
 * @returns { Object }
 */
async function getEquinoxRequestConfiguration() {

    const marketLocale = UrlService.getLocale();
    const [, market] = marketLocale.split("_");
    const equinoxMarket = (await getConfiguration(["Equinox_Markets"])).Equinox_Markets;

    const { accessToken } = sessionStorage.getItem("oktaTokens") ? JSON.parse(sessionStorage.getItem("oktaTokens")) : { accessToken : null };

    const shoppingContext = window && window.aem && window.aem.ShoppingContext && window.aem.ShoppingContext.getShoppingContext();

    if ((shoppingContext && shoppingContext.context === 'storefront') || window.location.hostname.includes(".mynuskin.com")) {
        equinoxMarket.API_Base_URLs = equinoxMarket.MySite_API_Base_URLs
    }

    if(accessToken) {
        defaultHeaders.Authorization = `Bearer ${accessToken}`;
    }

    const equinoxAPIUrl = equinoxMarket.API_Base_URLs

    return { equinoxMarket, equinoxAPIUrl, marketLocale, market }
}

/**
 * equinoxFavoritesMapper - removes item to a list in equinox for MySite Favorites
 * @param {Object} equinoxList - the list object from equinox
 * @returns {Object} an object containing the mapped favorite list items and metadata
 */
function equinoxFavoritesMapper(equinoxList) {
    if (!equinoxList) {
        return [];
    }

    if (!equinoxList.listItems || !equinoxList.listItems.length) {
        return {
            status: 200,
            messages: [],
            responseClass: "com.nuskin.services.shop.model.FavoriteResponse",
            FavoriteResponseList: []
        };
    }

    return {
        status: 200,
        messages: [],
        responseClass: "com.nuskin.services.shop.model.FavoriteResponse",
        FavoriteResponseList: equinoxList.listItems.map((equinoxFavorite) => ({
            ...equinoxFavorite,
            sku: equinoxFavorite.name,
            availableQuantity: 999,
            backOrderAvailableDate: null,
            market: null,
            country: "US",
            listId: equinoxList.listId
        }))
    };
}

/**
 * Equinox customer logout
 * @returns {Promise.<_equinoxRequest>} a promise resolving to the response of the request *
 */
function equinoxCustomerLogout() {
    const logoutRequestOption = {
        method: 'DELETE',
        endpoint: 'customers/logout'
    };

    return equinoxRequest(logoutRequestOption);
}


export default {
    getEquinoxSession,
    createEquinoxFavorite,
    getEquinoxFavorite,
    removeItemInEquinoxList,
    addItemToEquinoxList,
    getEquinoxRequestConfiguration,
    equinoxRequest,
    getEquinoxAccountType,
    equinoxSessionDetailMapper,
    getOrCreateFavorites,
    equinoxFavoritesMapper,
    equinoxCustomerLogout
}